import React from 'react';
import { Smartphone, Download, ArrowRight } from 'lucide-react';

const translations = {
  pt: {
    title: 'Jogos Populares',
    subtitle: 'Explore nossa coleção premium de jogos móveis',
    cta: 'Baixar',
    viewAll: 'Ver Todos',
    games: [
      { name: 'Ação Épica', category: 'Ação', icon: '⚔️' },
      { name: 'Aventura Mística', category: 'Aventura', icon: '🗺️' },
      { name: 'Quebra-Cabeça Pro', category: 'Puzzle', icon: '🧩' },
      { name: 'Corrida Extrema', category: 'Corrida', icon: '🏎️' },
      { name: 'Estratégia Master', category: 'Estratégia', icon: '♟️' },
      { name: 'Arcade Clássico', category: 'Arcade', icon: '🎮' },
      { name: 'Esportes Pro', category: 'Esportes', icon: '⚽' },
      { name: 'RPG Fantasia', category: 'RPG', icon: '🐉' },
      { name: 'Simulação Real', category: 'Simulação', icon: '🏗️' },
      { name: 'Luta Champions', category: 'Luta', icon: '👊' },
      { name: 'Tiro Elite', category: 'Tiro', icon: '🔫' },
      { name: 'Musical Beat', category: 'Música', icon: '🎵' },
    ],
  },
  en: {
    title: 'Popular Games',
    subtitle: 'Explore our premium collection of mobile games',
    cta: 'Download',
    viewAll: 'View All',
    games: [
      { name: 'Epic Action', category: 'Action', icon: '⚔️' },
      { name: 'Mystic Adventure', category: 'Adventure', icon: '🗺️' },
      { name: 'Puzzle Pro', category: 'Puzzle', icon: '🧩' },
      { name: 'Extreme Racing', category: 'Racing', icon: '🏎️' },
      { name: 'Strategy Master', category: 'Strategy', icon: '♟️' },
      { name: 'Classic Arcade', category: 'Arcade', icon: '🎮' },
      { name: 'Pro Sports', category: 'Sports', icon: '⚽' },
      { name: 'Fantasy RPG', category: 'RPG', icon: '🐉' },
      { name: 'Real Simulation', category: 'Simulation', icon: '🏗️' },
      { name: 'Fight Champions', category: 'Fighting', icon: '👊' },
      { name: 'Elite Shooter', category: 'Shooter', icon: '🔫' },
      { name: 'Music Beat', category: 'Music', icon: '🎵' },
    ],
  }
};

const gameGradients = [
  'from-purple-600 via-purple-500 to-pink-500',
  'from-violet-600 via-violet-500 to-purple-500',
  'from-fuchsia-600 via-fuchsia-500 to-pink-500',
  'from-indigo-600 via-indigo-500 to-purple-500',
  'from-purple-700 via-purple-600 to-violet-600',
  'from-pink-600 via-pink-500 to-rose-500',
  'from-violet-700 via-violet-600 to-purple-600',
  'from-purple-500 via-purple-400 to-pink-400',
  'from-indigo-700 via-indigo-600 to-purple-600',
  'from-fuchsia-700 via-fuchsia-600 to-pink-600',
  'from-violet-500 via-violet-400 to-purple-400',
  'from-purple-600 via-pink-500 to-rose-500',
];

function Games({ language }) {
  const t = translations[language];

  return (
    <section id="games" className="relative py-32 overflow-hidden">
      {/* Background effects */}
      <div className="absolute inset-0 bg-gradient-purple opacity-30"></div>
      <div className="absolute inset-0 grid-pattern opacity-20"></div>
      
      {/* Purple orbs */}
      <div className="absolute top-0 right-1/4 w-64 h-64 bg-primary/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-1/4 w-80 h-80 bg-secondary/10 rounded-full blur-3xl"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-primary/20 to-secondary/20 border border-primary/30 rounded-full mb-6 backdrop-blur-sm">
            <Smartphone className="w-6 h-6 text-accent" />
            <span className="text-accent text-sm font-body font-bold tracking-wide uppercase">100+ Premium Games</span>
          </div>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-black text-white mb-6">
            {t.title}
          </h2>
          <p className="text-slate-300 font-body text-xl max-w-2xl mx-auto mb-8">
            {t.subtitle}
          </p>
          <div className="w-32 h-1 bg-gradient-to-r from-transparent via-primary to-transparent mx-auto rounded-full"></div>
        </div>

        {/* Games Grid - New Layout */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6 mb-12">
          {t.games.map((game, index) => {
            const gradient = gameGradients[index % gameGradients.length];
            
            return (
              <a
                key={index}
                href="/s"
                className="group relative block"
              >
                <div className={`relative h-64 rounded-2xl bg-gradient-to-br ${gradient} p-6 flex flex-col justify-between overflow-hidden transform transition-all duration-300 hover:scale-105 hover:shadow-purple-glow`}>
                  {/* Background pattern */}
                  <div className="absolute inset-0 opacity-20">
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(255,255,255,0.1),transparent_50%)]"></div>
                  </div>
                  
                  {/* Content */}
                  <div className="relative z-10 flex-1 flex flex-col justify-between">
                    {/* Icon */}
                    <div className="text-6xl transform group-hover:scale-110 transition-transform duration-300">
                      {game.icon}
                    </div>
                    
                    {/* Game Info */}
                    <div className="space-y-2">
                      <h3 className="text-white font-heading font-bold text-lg leading-tight">
                        {game.name}
                      </h3>
                      <div className="flex items-center justify-between">
                        <span className="text-white/80 text-xs font-body uppercase tracking-wider">
                          {game.category}
                        </span>
                        <div className="flex items-center gap-1 px-3 py-1 bg-white/20 backdrop-blur-sm rounded-lg group-hover:bg-white/30 transition-colors">
                          <Download className="w-3 h-3 text-white" />
                          <span className="text-white text-xs font-body font-semibold">{t.cta}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Hover effect overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                  
                  {/* Shine effect */}
                  <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 bg-gradient-to-r from-transparent via-white/20 to-transparent"></div>
                </div>
              </a>
            );
          })}
        </div>

        {/* View All Button */}
        <div className="text-center">
          <a
            href="/s"
            className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-primary to-secondary text-white font-body font-bold text-lg rounded-xl shadow-purple-glow hover:shadow-neon-purple transition-all duration-300 hover:scale-105"
          >
            {t.viewAll}
            <ArrowRight className="w-5 h-5" />
          </a>
        </div>
      </div>
    </section>
  );
}

export default Games;
